import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { useInterestedFamilies } from "@/hooks/useScheduleOptions";

interface Props {
  open: boolean;
  onClose: () => void;
  dbClassId: string | null | undefined;
  displayLabel: string | null;
  optionLabel: string;
}

/**
 * Admin view of every interest submission for one schedule option.
 * Preserves original submission history (duplicates included) so admins can
 * audit; a "unique families" count is shown separately.
 */
export default function InterestedFamiliesDialog({ open, onClose, dbClassId, displayLabel, optionLabel }: Props) {
  const { families, loading } = useInterestedFamilies(dbClassId, displayLabel);

  const uniqueEmails = new Set(
    families.map((f) => (f.email ?? "").trim().toLowerCase()).filter(Boolean),
  );

  const exportCsv = () => {
    const header = ["Submitted", "Adult", "Email", "Child", "Age"];
    const rows = families.map((f) => [
      new Date(f.created_at).toISOString(),
      f.adult_name ?? "",
      f.email ?? "",
      f.child_name ?? "",
      f.child_age == null ? "" : String(f.child_age),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `interest-${optionLabel.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Interested families — {optionLabel}</DialogTitle>
          <DialogDescription>
            {loading ? "Loading…" : `${uniqueEmails.size} unique families · ${families.length} total submissions`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={families.length === 0}>
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto rounded-md border">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
            </div>
          ) : families.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No families have joined this interest list yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Submitted</th>
                  <th className="text-left px-3 py-2">Adult</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Child</th>
                  <th className="text-left px-3 py-2">Age</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {families.map((f) => (
                  <tr key={f.id}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(f.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2">{f.adult_name ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">{f.email ?? "—"}</td>
                    <td className="px-3 py-2">{f.child_name ?? "—"}</td>
                    <td className="px-3 py-2">{f.child_age ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
